// Animated wallpaper store (GIF / MP4 / WebM). Too big for localStorage, so the file itself lives
// in IndexedDB ("revault-wallpaper", store "media", one record) and only a small index record
// (type, size, a ~480px poster frame) sits in localStorage under WALLPAPER_MEDIA_KEY. The index
// lets the gallery and the app paint a still frame instantly and syncs every tab through store.js;
// the blob is read only when the wallpaper actually plays. Like music uploads, it stays in this
// browser (not in backups or shared look files).
import { WALLPAPER_MEDIA_KEY } from "./settings-schema.js";
import { readJSON, writeJSON, remove, subscribe } from "./store.js";

export const MAX_BYTES = 40 * 1024 * 1024;
const DB = "revault-wallpaper";

let dbp = null;
function openDB() {
	if (dbp) return dbp;
	dbp = new Promise((resolve, reject) => {
		// v2 adds "slots": a supporter's saved wallpapers (docs/SUPPORTERS.md 8).
		const r = indexedDB.open(DB, 2);
		r.onupgradeneeded = () => {
			if (!r.result.objectStoreNames.contains("media")) r.result.createObjectStore("media");
			if (!r.result.objectStoreNames.contains("slots")) r.result.createObjectStore("slots");
		};
		r.onsuccess = () => { const db = r.result; db.onversionchange = () => { try { db.close(); } catch (e) {} dbp = null; }; resolve(db); };
		r.onerror = () => { dbp = null; reject(r.error || new Error("IndexedDB unavailable")); };
	});
	return dbp;
}
async function tx(mode, fn, store = "media") {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const t = db.transaction(store, mode);
		const out = fn(t.objectStore(store));
		t.oncomplete = () => resolve(out && "result" in out ? out.result : undefined);
		t.onerror = () => reject(t.error);
		t.onabort = () => reject(t.error || new Error("aborted"));
	});
}

// The file's first bytes decide its kind; the declared MIME type alone is not trusted.
export function sniffKind(bytes) {
	const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
	if (b.length >= 6 && b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38) return "gif"; // GIF8
	if (b.length >= 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) return "mp4"; // ....ftyp
	if (b.length >= 4 && b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) return "webm"; // EBML
	return null;
}
export const isVideoKind = (k) => k === "mp4" || k === "webm";
// A wallpaper link that points at a video file plays in the video layer (GIF links already animate).
export const isVideoUrl = (u) => /^https?:\/\/[^?#]+\.(mp4|webm|m4v|mov)(?:[?#].*)?$/i.test(String(u || ""));

export function getMediaInfo() {
	const v = readJSON(WALLPAPER_MEDIA_KEY, null);
	return v && typeof v === "object" && (v.kind === "gif" || isVideoKind(v.kind)) ? v : null;
}
export const subscribeMedia = (fn) => subscribe(WALLPAPER_MEDIA_KEY, fn);

export async function getMediaBlob() {
	try { const rec = await tx("readonly", (s) => s.get("current")); return rec && rec.blob ? rec.blob : null; }
	catch (e) { return null; }
}

// A small JPEG of the first frame: the gallery thumbnail, the instant first paint, and the still
// frame shown when motion is off (Lite mode, reduced motion, or the setting).
async function posterFor(blob, kind) {
	const url = URL.createObjectURL(blob);
	try {
		let src;
		if (isVideoKind(kind)) {
			const v = document.createElement("video");
			v.muted = true; v.playsInline = true; v.preload = "auto"; v.src = url;
			await new Promise((res, rej) => { v.onloadeddata = res; v.onerror = () => rej(new Error("decode")); setTimeout(() => rej(new Error("timeout")), 15000); });
			try { v.currentTime = Math.min(0.1, (v.duration || 1) / 10); await new Promise((r) => { v.onseeked = r; setTimeout(r, 1500); }); } catch (e) {}
			src = v;
		} else {
			const img = new Image();
			img.src = url;
			await img.decode();
			src = img;
		}
		const w0 = src.videoWidth || src.naturalWidth, h0 = src.videoHeight || src.naturalHeight;
		if (!w0 || !h0) throw new Error("decode");
		const scale = Math.min(1, 480 / Math.max(w0, h0));
		const c = document.createElement("canvas");
		c.width = Math.max(1, Math.round(w0 * scale)); c.height = Math.max(1, Math.round(h0 * scale));
		c.getContext("2d").drawImage(src, 0, 0, c.width, c.height);
		return { poster: c.toDataURL("image/jpeg", 0.72), width: w0, height: h0 };
	} finally { URL.revokeObjectURL(url); }
}

// Validates, stores and announces an uploaded GIF / MP4 / WebM. Rejects with a user-facing message.
export async function setMediaFromFile(file) {
	if (!file) throw new Error("Choose a GIF or a video.");
	if (file.size > MAX_BYTES) throw new Error("That file is " + Math.round(file.size / 1048576) + " MB. Animated wallpapers can be up to 40 MB.");
	const kind = sniffKind(await file.slice(0, 16).arrayBuffer());
	if (!kind) throw new Error("That isn't a GIF, MP4 or WebM file.");
	try {
		const est = navigator.storage && navigator.storage.estimate ? await navigator.storage.estimate() : null;
		if (est && est.quota - est.usage < file.size + 16 * 1048576) throw new Error("quota");
	} catch (e) { if (e && e.message === "quota") throw new Error("Not enough browser storage for that file. Free some space and try again."); }
	let meta;
	try { meta = await posterFor(file, kind); } catch (e) { throw new Error("This browser can't play that file."); }
	try { await tx("readwrite", (s) => s.put({ blob: file, kind, addedAt: Date.now() }, "current")); }
	catch (e) { throw new Error("Couldn't save the file in this browser (storage may be full)."); }
	// Commit the blob first, then publish the index record (which every tab listens to).
	writeJSON(WALLPAPER_MEDIA_KEY, { kind, name: String(file.name || "").slice(0, 120), size: file.size, poster: meta.poster, width: meta.width, height: meta.height, addedAt: Date.now() });
}

export async function clearMedia() {
	try { await tx("readwrite", (s) => s.delete("current")); } catch (e) {}
	remove(WALLPAPER_MEDIA_KEY);
}

// --- saved wallpapers (a Supporter perk) --------------------------------------------------------
// Up to SLOT_MAX files in the "slots" store, with a small index (kind, name, poster) under
// WALLPAPER_SLOTS_KEY. Saving needs the perk (the caller checks); using, downloading and deleting
// never do, so nothing is lost when a pass ends.
export const WALLPAPER_SLOTS_KEY = "revault:wallpaper-slots";
export const SLOT_MAX = 10;
export const listSlots = () => { const v = readJSON(WALLPAPER_SLOTS_KEY, []); return Array.isArray(v) ? v : []; };
export const subscribeSlots = (fn) => subscribe(WALLPAPER_SLOTS_KEY, fn);

// Saves the wallpaper in use (an uploaded picture, GIF or video) as a new slot.
export async function saveSlot({ kind, blob, name, poster }) {
	const list = listSlots();
	if (list.length >= SLOT_MAX) throw new Error("You have " + SLOT_MAX + " saved wallpapers. Delete one first.");
	const id = "w" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
	try { await tx("readwrite", (st) => st.put({ blob, kind }, id), "slots"); }
	catch { throw new Error("Couldn't save the file in this browser (storage may be full)."); }
	writeJSON(WALLPAPER_SLOTS_KEY, [...list, { id, kind, name: String(name || "").slice(0, 80), poster: poster || "", size: blob.size || 0, addedAt: Date.now() }]);
	return id;
}
export async function slotBlob(id) {
	try { const rec = await tx("readonly", (st) => st.get(id), "slots"); return rec || null; } catch { return null; }
}
export async function deleteSlot(id) {
	try { await tx("readwrite", (st) => st.delete(id), "slots"); } catch { /* already gone */ }
	writeJSON(WALLPAPER_SLOTS_KEY, listSlots().filter((x) => x.id !== id));
}
// Puts a saved GIF or video back as the current animated wallpaper.
export async function useMediaSlot(id) {
	const rec = await slotBlob(id);
	const meta = listSlots().find((x) => x.id === id);
	if (!rec || !meta) throw new Error("That saved wallpaper isn't in this browser any more.");
	await tx("readwrite", (st) => st.put({ blob: rec.blob, kind: rec.kind, addedAt: Date.now() }, "current"));
	writeJSON(WALLPAPER_MEDIA_KEY, { kind: rec.kind, name: meta.name, size: rec.blob.size, poster: meta.poster, width: 0, height: 0, addedAt: Date.now() });
}
